import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Table, Row, Col, Card, CardBody, Input, Badge, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const SubClientList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [subClients, setSubClients] = useState([]);
    const [summary, setSummary] = useState({
        total_sub_clients: 0,
        active_sub_clients: 0,
        inactive_sub_clients: 0
    });
    
    const [pagination, setPagination] = useState({ 
        current_page: 1, 
        total_pages: 1, 
        total_records: 0 
    });

    const [filters, setFilters] = useState({
        name: "",
        status: "",
        page: 1,
        limit: 10
    });

    const loadSubClients = async () => {
        dispatch(loadingTrue());
        try {
            const queryParams = new URLSearchParams({
                ...filters,
                status: filters.status
            }).toString();
            
            const res = await Handler({ method: "GET", url: `/sub_client/list.php?${queryParams}` });

            if (res?.success) {
                setSubClients(res?.data || []);
                setSummary(res?.summary);
                setPagination({
                    current_page: res?.current_page || 1,
                    total_pages: res?.total_pages || 1,
                    total_records: res?.total_records || 0
                });
            } else {
                toast.error(res?.message || "Failed to fetch sub-clients");
            }
        } catch (err) {
            toast.error("Server connection error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    // Toggle Status Logic
    const handleStatusToggle = async (id, currentStatus) => {
        const newStatus = currentStatus === 1 ? 0 : 1;
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to ${newStatus === 1 ? 'Activate' : 'Deactivate'} this sub-client?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#f16436',
            confirmButtonText: 'Yes, update it!'
        });

        if (result.isConfirmed) {
            dispatch(loadingTrue());
            try {
                // Machan inga unga update.php url correct-ah nu check panniko
                const res = await Handler({
                    method: "POST",
                    url: "/sub_client/update.php", 
                    data: { id: id, status: newStatus }
                });

                if (res?.success) {
                    toast.success("Status Updated Successfully");
                    loadSubClients();
                } else {
                    toast.error(res?.message || "Failed to update status");
                }
            } catch (e) {
                toast.error("Error updating status");
            } finally {
                dispatch(loadingFalse());
            }
        }
    };

    useEffect(() => { loadSubClients(); }, [filters.page, filters.status]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value, page: 1 });
    };

    const handleSearch = (e) => { if (e.key === 'Enter') loadSubClients(); };

    return (
        <div className="page-container p-1">
            <div className="page-header mb-4">
                <h6 className="text-muted">Management &gt; Sub-Clients</h6>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="fw-bold ">Sub-Client Directory</h3>
                        <p className="text-muted small">
                            Total: {summary.total_sub_clients} | 
                            Active: {summary.active_sub_clients} | 
                            Inactive: {summary.inactive_sub_clients}
                        </p>
                    </div>
                    <Button color="danger" className="px-4 shadow-sm" style={{backgroundColor: '#f16436', border: 'none'}} onClick={() => navigate("/sub-clients/add")}>
                        <i className="bx bx-plus"></i> Add Sub-Client
                    </Button>
                </div>
            </div>

            {/* Filter Section */}
            <Card className="border-0 shadow-sm mb-4 rounded-4">
                <CardBody className="p-3">
                    <Row className="g-3">
                        <Col md={6}>
                            <Input 
                                name="name" 
                                style={{height:'45px'}} 
                                placeholder="Search by name, email or phone..." 
                                value={filters.name} 
                                onChange={handleFilterChange} 
                                onKeyDown={handleSearch} 
                            />
                        </Col>
                        <Col md={4}>
                            <Input 
                                type="select" 
                                style={{height:'45px'}} 
                                name="status" 
                                value={filters.status} 
                                onChange={handleFilterChange}
                            >
                                <option value="">All Status</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </Input>
                        </Col>
                        <Col md={2}>
                            <Button color="primary" style={{height:'45px'}} block onClick={loadSubClients}>
                                <i className="bx bx-search-alt me-1"></i> Search
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Table Section */}
            <Card className="border-0 shadow-sm rounded-4">
                <CardBody className="p-0">
                    <Table responsive hover className="mb-0 align-middle">
                        <thead className="bg-light text-muted small text-uppercase">
                            <tr>
                                <th className="ps-4">S.No</th>
                                <th>Sub-Client Details</th>
                                <th>Parent Merchant</th>
                                <th>Contact & GST</th>
                                <th>Status</th>
                                <th className="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {subClients.length > 0 ? (
                                subClients.map((item, index) => (
                                    <tr key={item.id}>
                                        <td className="ps-4">{(filters.page - 1) * filters.limit + index + 1}</td>
                                        <td>
                                            <div className="fw-bold text-dark">{item.sub_client.name?.toUpperCase()}</div>
                                            <small className="text-muted text-capitalize">{item.sub_client.address || "No Address"}</small>
                                        </td>
                                        <td>
                                            <Badge color="light" className="text-dark border">
                                                <i className="bx bx-link-alt me-1"></i>
                                                {item.parent_client.name?.toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className="small"><i className="bx bx-phone me-1"></i>{item.sub_client.phone}</div>
                                            <div className="small text-muted"><i className="bx bx-barcode me-1"></i>{item.sub_client.gstin || "No GST"}</div>
                                        </td>
                                        <td>
                                            <div className="form-check form-switch d-flex align-items-center">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={item.status.value === 1}
                                                    onChange={() => handleStatusToggle(item.id, item.status.value)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                <Badge color={item.status.value === 1 ? "success" : "danger"} pill className="ms-2">
                                                    {item.status.label}
                                                </Badge>
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <Button color="light" size="sm" className="rounded-3 shadow-sm border" onClick={() => navigate(`/sub-clients/${item.id}`)}>
                                                <i className="bx bx-edit text-primary fs-5"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="6" className="text-center py-5 text-muted italic">No sub-client records found.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </CardBody>
            </Card>

            {/* Pagination Section */}
            {pagination?.total_pages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination className="pagination-rounded shadow-sm">
                        <PaginationItem disabled={filters.page <= 1}>
                            <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters.page - 1 })} />
                        </PaginationItem>
                        {[...Array(pagination?.total_pages)].map((_, i) => (
                            <PaginationItem active={i + 1 === filters.page} key={i}>
                                <PaginationLink onClick={() => setFilters({ ...filters, page: i + 1 })}>{i + 1}</PaginationLink>
                            </PaginationItem>
                        ))}
                        <PaginationItem disabled={filters.page >= pagination?.total_pages}>
                            <PaginationLink next onClick={() => setFilters({ ...filters, page: filters.page + 1 })} />
                        </PaginationItem>
                    </Pagination>
                </div>
            )}
        </div>
    );
};

export default SubClientList;